import React, { useState, useEffect, useLayoutEffect, useId, useMemo, useRef, useCallback } from 'react';
import { apiRequest } from '../config/api';
import {
  SkewTPoint,
  SkewTOptions,
  pressureToY,
  temperatureToX,
  getIsobars,
  getIsotherms,
  getDryAdiabats,
  getMoistAdiabats,
  interpolateTwoColors,
  processSkewTData,
  calculateFittedTemperatureBounds,
  generateDashedSegments,
  RawMeasurement,
  RawCoordinate
} from '../utils/skewTUtils';

export interface SkewTChartProps {
  flightId?: string;
  measurements?: RawMeasurement[] | null;
  coordinates?: RawCoordinate[];
  dateStart?: string;
  dateEnd?: string;
  hoverTimestamp?: number | null;
  onHoverTimestamp?: (timestamp: number | null) => void;
  onClickTimestamp?: (timestamp: number) => void;
}

const START_COLOR = '#2563eb'; // Vibrant Blue (Flight Start)
const END_COLOR = '#dc2626';   // Warm Red (Flight End)
const DEWPOINT_START_COLOR = '#10b981'; // Vibrant Emerald / Mint (Flight Start)
const DEWPOINT_END_COLOR = '#065f46';   // Deep Forest Green (Flight End)
const FREEZING_COLOR = '#3b82f6'; // Freezing level (0°C) blue

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

const SkewTChart: React.FC<SkewTChartProps> = ({
  flightId,
  measurements: propMeasurements,
  coordinates = [],
  hoverTimestamp,
  onHoverTimestamp,
  onClickTimestamp,
}) => {
  const clipId = useId();

  const [internalMeasurements, setInternalMeasurements] = useState<RawMeasurement[]>([]);
  const [loading, setLoading] = useState(false);

  // Layers (Temperature and Dewpoint are always active)
  const showTemperature = true;
  const showDewpoint = true;
  const [showDryAdiabats, setShowDryAdiabats] = useState(true);
  const [showMoistAdiabats, setShowMoistAdiabats] = useState(false);
  const [fitPressure, setFitPressure] = useState(true);

  // Interactive hover state
  const [activePoint, setActivePoint] = useState<SkewTPoint | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(() => {
    if (typeof window !== 'undefined' && window.innerWidth) {
      return Math.max(320, window.innerWidth - 80);
    }
    return 1000;
  });

  // Fetch measurements if not provided directly
  useEffect(() => {
    if (propMeasurements !== undefined && propMeasurements !== null) {
      setInternalMeasurements(propMeasurements);
      return;
    }
    if (!flightId) return;

    const fetchSensorData = async () => {
      setLoading(true);
      try {
        const res = await apiRequest(`/sensor-data/${flightId}`);
        if (res.ok) {
          const data = await res.json();
          setInternalMeasurements(data.measurements || []);
        }
      } catch {
        setInternalMeasurements([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSensorData();
  }, [flightId, propMeasurements]);

  // Responsive width tracking
  const updateWidth = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) {
      setContainerWidth(Math.floor(rect.width));
    }
  }, []);

  useLayoutEffect(() => {
    updateWidth();
  }, [updateWidth]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    updateWidth();

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0) {
          setContainerWidth(Math.floor(entry.contentRect.width));
        }
      }
    });

    observer.observe(el);
    window.addEventListener('resize', updateWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [updateWidth, loading]);

  // Process and downsample flight sounding points
  const points = useMemo(() => {
    return processSkewTData(internalMeasurements, coordinates, 600);
  }, [internalMeasurements, coordinates]);

  const hasDewpoint = useMemo(() => {
    return points.some(p => p.dewpoint !== undefined);
  }, [points]);

  // Chart dimensions and layout (maximizes width across the card)
  const height = 480;
  const margin = { top: 20, right: 16, bottom: 44, left: 64 };
  const plotWidth = Math.max(200, containerWidth - margin.left - margin.right);
  const plotHeight = height - margin.top - margin.bottom;

  // Pressure range (standard 1050-100 hPa vs. auto-fit to flight pressure)
  const { pMin, pMax } = useMemo(() => {
    if (!fitPressure || points.length === 0) {
      return { pMin: 100, pMax: 1050 };
    }
    let minP = Infinity;
    let maxP = -Infinity;
    for (const p of points) {
      if (p.pressure < minP) minP = p.pressure;
      if (p.pressure > maxP) maxP = p.pressure;
    }
    if (!isFinite(minP) || !isFinite(maxP) || minP >= maxP) {
      return { pMin: 100, pMax: 1050 };
    }
    // Add 15 hPa headroom/footroom and round to 25 hPa steps
    const fittedMin = Math.max(100, Math.floor((minP - 15) / 25) * 25);
    const fittedMax = Math.min(1050, Math.ceil((maxP + 15) / 25) * 25);
    return { pMin: fittedMin, pMax: fittedMax };
  }, [fitPressure, points]);

  // Skew angle (standard 45°, adapting gently on narrow viewports so isotherms stay well-proportioned)
  const skewAngleDeg = useMemo(() => {
    const maxSkew = plotWidth * 0.52;
    if (plotHeight > 0 && maxSkew < plotHeight) {
      return Math.round((Math.atan(maxSkew / plotHeight) * 180) / Math.PI);
    }
    return 45;
  }, [plotWidth, plotHeight]);

  // Temperature axis range taking skew displacement into account so curves never escape
  const { tMin, tMax } = useMemo(() => {
    return calculateFittedTemperatureBounds(
      points,
      pMin,
      pMax,
      plotWidth,
      plotHeight,
      skewAngleDeg,
      fitPressure
    );
  }, [points, pMin, pMax, plotWidth, plotHeight, skewAngleDeg, fitPressure]);

  const skewOptions: SkewTOptions = useMemo(() => ({
    plotLeft: margin.left,
    plotTop: margin.top,
    plotWidth,
    plotHeight,
    pMin,
    pMax,
    tMin,
    tMax,
    skewAngleDeg
  }), [margin.left, margin.top, plotWidth, plotHeight, pMin, pMax, tMin, tMax, skewAngleDeg]);

  // Generate thermodynamic reference grids
  const isobars = useMemo(() => getIsobars(skewOptions), [skewOptions]);
  const isotherms = useMemo(() => getIsotherms(skewOptions, 10), [skewOptions]);
  const dryAdiabats = useMemo(() => getDryAdiabats(skewOptions), [skewOptions]);
  const moistAdiabats = useMemo(() => getMoistAdiabats(skewOptions), [skewOptions]);

  // Map points to screen coordinates
  const mappedPoints = useMemo(() => {
    if (points.length === 0) return [];
    return points.map(p => {
      const x = temperatureToX(p.temperature, p.pressure, skewOptions);
      const y = pressureToY(p.pressure, skewOptions);
      const xTd = p.dewpoint !== undefined ? temperatureToX(p.dewpoint, p.pressure, skewOptions) : undefined;
      return { ...p, x, y, xTd };
    });
  }, [points, skewOptions]);

  // Sync active hover point if hoverTimestamp is provided externally (e.g. from RouteMap)
  useEffect(() => {
    if (hoverTimestamp === null || hoverTimestamp === undefined) {
      if (!mousePos) setActivePoint(null);
      return;
    }
    if (mappedPoints.length === 0) return;

    // Find closest point by timestamp
    let closest = mappedPoints[0];
    let minDiff = Math.abs(closest.timestamp - hoverTimestamp);
    for (let i = 1; i < mappedPoints.length; i++) {
      const diff = Math.abs(mappedPoints[i].timestamp - hoverTimestamp);
      if (diff < minDiff) {
        minDiff = diff;
        closest = mappedPoints[i];
      }
    }
    setActivePoint(closest);
  }, [hoverTimestamp, mappedPoints, mousePos]);

  // Mouse hover handler
  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (mappedPoints.length === 0) return;
    const svgRect = e.currentTarget.getBoundingClientRect();
    const curX = e.clientX - svgRect.left;
    const curY = e.clientY - svgRect.top;
    setMousePos({ x: curX, y: curY });

    // Check if mouse is within plot area
    if (
      curX < margin.left ||
      curX > margin.left + plotWidth ||
      curY < margin.top ||
      curY > margin.top + plotHeight
    ) {
      return;
    }

    // Find nearest sounding point by 2D screen distance to either temperature or dewpoint curve
    let nearest = mappedPoints[0];
    let minDist = Infinity;

    for (const p of mappedPoints) {
      const dxT = p.x - curX;
      const dy = p.y - curY;
      let dist = dxT * dxT + dy * dy;

      if (showDewpoint && p.xTd !== undefined) {
        const dxTd = p.xTd - curX;
        const distTd = dxTd * dxTd + dy * dy;
        if (distTd < dist) {
          dist = distTd;
        }
      }

      if (dist < minDist) {
        minDist = dist;
        nearest = p;
      }
    }

    // Only select if within reasonable distance (120px)
    if (minDist < 120 * 120) {
      setActivePoint(nearest);
      onHoverTimestamp?.(nearest.timestamp);
    } else {
      setActivePoint(null);
      onHoverTimestamp?.(null);
    }
  }, [mappedPoints, margin.left, margin.top, plotWidth, plotHeight, onHoverTimestamp, showDewpoint]);

  const handleMouseLeave = useCallback(() => {
    setMousePos(null);
    setActivePoint(null);
    onHoverTimestamp?.(null);
  }, [onHoverTimestamp]);

  const handleClick = useCallback(() => {
    if (activePoint && onClickTimestamp) {
      onClickTimestamp(activePoint.timestamp);
    }
  }, [activePoint, onClickTimestamp]);

  // Pre-filter mapped points that have valid dewpoint
  const dewpointPoints = useMemo(() => {
    return mappedPoints.filter(p => p.xTd !== undefined);
  }, [mappedPoints]);

  // Compute physical dashed segments with per-dash color gradient factor
  const dewpointDashes = useMemo(() => {
    if (!showDewpoint || dewpointPoints.length < 2) return [];
    const pts = dewpointPoints.map((p, i) => ({
      x: p.xTd!,
      y: p.y,
      factor: points.length > 1
        ? (p.timestamp - points[0].timestamp) / Math.max(1, points[points.length - 1].timestamp - points[0].timestamp)
        : i / (dewpointPoints.length - 1)
    }));
    return generateDashedSegments(pts, 6, 5);
  }, [showDewpoint, dewpointPoints, points]);

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative', userSelect: 'none' }}>
      {loading ? (
        <div style={{ padding: '32px', textAlign: 'center', color: '#687088' }}>
          <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
          Lade Skew-T Sounding-Daten…
        </div>
      ) : points.length < 2 ? (
        <div style={{
          height: 240,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9aa0ad',
          gap: '8px'
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p style={{ margin: 0, fontSize: '13px' }}>
            Keine Temperatur- und Luftdruckdaten für dieses Diagramm verfügbar.
          </p>
        </div>
      ) : (
        <>
          {/* Controls Bar */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          {/* Dry Adiabats Toggle */}
          <button
            type="button"
            onClick={() => setShowDryAdiabats(v => !v)}
            style={{
              padding: '3px 12px',
              borderRadius: '20px',
              border: `1.5px solid ${showDryAdiabats ? '#d97706' : '#dde1ea'}`,
              background: showDryAdiabats ? '#fffbeb' : 'white',
              color: showDryAdiabats ? '#b45309' : '#687088',
              fontSize: '12px',
              fontWeight: showDryAdiabats ? 600 : 400,
              cursor: 'pointer'
            }}
          >
            Trockenadiabaten
          </button>

          {/* Moist Adiabats Toggle */}
          <button
            type="button"
            onClick={() => setShowMoistAdiabats(v => !v)}
            style={{
              padding: '3px 12px',
              borderRadius: '20px',
              border: `1.5px solid ${showMoistAdiabats ? '#0d9488' : '#dde1ea'}`,
              background: showMoistAdiabats ? '#f0fdfa' : 'white',
              color: showMoistAdiabats ? '#0f766e' : '#687088',
              fontSize: '12px',
              fontWeight: showMoistAdiabats ? 600 : 400,
              cursor: 'pointer'
            }}
          >
            Feuchtadiabaten
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          {/* Scale to fit toggle */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            background: '#f1f5f9',
            borderRadius: '20px',
            padding: '2px',
            border: '1px solid #e2e8f0'
          }}>
            <button
              type="button"
              onClick={() => setFitPressure(true)}
              style={{
                padding: '3px 10px',
                borderRadius: '16px',
                border: 'none',
                background: fitPressure ? 'white' : 'transparent',
                color: fitPressure ? '#0284c7' : '#687088',
                fontWeight: fitPressure ? 600 : 400,
                fontSize: '11px',
                cursor: 'pointer',
                boxShadow: fitPressure ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="8 3 12 7 16 3" />
                <line x1="12" y1="7" x2="12" y2="17" />
                <polyline points="8 21 12 17 16 21" />
              </svg>
              Einpassen
            </button>
            <button
              type="button"
              onClick={() => setFitPressure(false)}
              style={{
                padding: '3px 10px',
                borderRadius: '16px',
                border: 'none',
                background: !fitPressure ? 'white' : 'transparent',
                color: !fitPressure ? '#1e293b' : '#687088',
                fontWeight: !fitPressure ? 600 : 400,
                fontSize: '11px',
                cursor: 'pointer',
                boxShadow: !fitPressure ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s ease'
              }}
            >
              Standard (1050–100 hPa)
            </button>
          </div>
        </div>
      </div>

      {/* SVG Diagram */}
      <div style={{ position: 'relative', width: '100%', height }}>
        <svg
          width={containerWidth}
          height={height}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          style={{ display: 'block', cursor: activePoint ? 'pointer' : 'crosshair' }}
        >
          <defs>
            {/* Clip path to restrain flight lines within plot borders */}
            <clipPath id={clipId}>
              <rect
                x={margin.left}
                y={margin.top}
                width={plotWidth}
                height={plotHeight}
              />
            </clipPath>
          </defs>

          {/* Background Plot Area */}
          <rect
            x={margin.left}
            y={margin.top}
            width={plotWidth}
            height={plotHeight}
            fill="#ffffff"
            stroke="#e2e8f0"
            strokeWidth="1"
          />

          {/* Clipped Background Reference Lines */}
          <g clipPath={`url(#${clipId})`}>
            {/* Dry Adiabats */}
            {showDryAdiabats && dryAdiabats.map((curve, idx) => {
              const d = curve.points.reduce((acc, pt, i) => `${acc} ${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(1)},${pt.y.toFixed(1)}`, '');
              return (
                <path
                  key={`dry-${idx}`}
                  d={d}
                  fill="none"
                  stroke="#fbbf24"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                  opacity="0.6"
                />
              );
            })}

            {/* Moist Adiabats */}
            {showMoistAdiabats && moistAdiabats.map((curve, idx) => {
              const d = curve.points.reduce((acc, pt, i) => `${acc} ${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(1)},${pt.y.toFixed(1)}`, '');
              return (
                <path
                  key={`moist-${idx}`}
                  d={d}
                  fill="none"
                  stroke="#2dd4bf"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  opacity="0.6"
                />
              );
            })}

            {/* Slanted Isotherms */}
            {isotherms.map((iso) => (
              <line
                key={`iso-${iso.temperature}`}
                x1={iso.x1}
                y1={iso.y1}
                x2={iso.x2}
                y2={iso.y2}
                stroke={iso.isFreezing ? FREEZING_COLOR : '#f1f5f9'}
                strokeWidth={iso.isFreezing ? 1.5 : 1}
                strokeDasharray={iso.isFreezing ? '4 3' : undefined}
                opacity={iso.isFreezing ? 0.85 : 1}
              />
            ))}

            {/* Horizontal Isobars */}
            {isobars.map((bar) => (
              <line
                key={`bar-${bar.pressure}`}
                x1={margin.left}
                y1={bar.y}
                x2={margin.left + plotWidth}
                y2={bar.y}
                stroke="#e2e8f0"
                strokeWidth="1"
                strokeDasharray="3 3"
              />
            ))}

            {/* Dewpoint Flight Curve (Two-Color Green Gradient, Guaranteed Dashed) */}
            {showDewpoint && dewpointDashes.length > 0 && (
              <g>
                {dewpointDashes.map((d, i) => (
                  <line
                    key={`dew-dash-${i}`}
                    x1={d.x1}
                    y1={d.y1}
                    x2={d.x2}
                    y2={d.y2}
                    stroke={interpolateTwoColors(DEWPOINT_START_COLOR, DEWPOINT_END_COLOR, d.factor)}
                    strokeWidth="2.4"
                    strokeLinecap="round"
                  />
                ))}
              </g>
            )}

            {/* Temperature Flight Curve (Two-Color Gradient along path) */}
            {showTemperature && mappedPoints.length > 1 && (
              <g>
                {mappedPoints.slice(0, mappedPoints.length - 1).map((p, i) => {
                  const nextP = mappedPoints[i + 1];
                  const factor = points.length > 1
                    ? (p.timestamp - points[0].timestamp) / Math.max(1, points[points.length - 1].timestamp - points[0].timestamp)
                    : i / (mappedPoints.length - 1);
                  const segColor = interpolateTwoColors(START_COLOR, END_COLOR, factor);
                  return (
                    <line
                      key={`seg-${i}`}
                      x1={p.x}
                      y1={p.y}
                      x2={nextP.x}
                      y2={nextP.y}
                      stroke={segColor}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  );
                })}
              </g>
            )}

            {/* Active Hover Crosshair and Highlights */}
            {activePoint && (
              <g>
                {/* Horizontal isobar line */}
                <line
                  x1={margin.left}
                  y1={pressureToY(activePoint.pressure, skewOptions)}
                  x2={margin.left + plotWidth}
                  y2={pressureToY(activePoint.pressure, skewOptions)}
                  stroke="#94a3b8"
                  strokeWidth="1"
                  strokeDasharray="2 2"
                />

                {/* Slanted isotherm line at hover temperature */}
                <line
                  x1={temperatureToX(activePoint.temperature, skewOptions.pMax, skewOptions)}
                  y1={pressureToY(skewOptions.pMax, skewOptions)}
                  x2={temperatureToX(activePoint.temperature, skewOptions.pMin, skewOptions)}
                  y2={pressureToY(skewOptions.pMin, skewOptions)}
                  stroke="#94a3b8"
                  strokeWidth="1"
                  strokeDasharray="2 2"
                />

                {/* Dewpoint marker with interpolated flight color */}
                {showDewpoint && activePoint.dewpoint !== undefined && (
                  <circle
                    cx={temperatureToX(activePoint.dewpoint, activePoint.pressure, skewOptions)}
                    cy={pressureToY(activePoint.pressure, skewOptions)}
                    r="5.5"
                    fill={interpolateTwoColors(
                      DEWPOINT_START_COLOR,
                      DEWPOINT_END_COLOR,
                      points.length > 1
                        ? (activePoint.timestamp - points[0].timestamp) /
                          Math.max(1, points[points.length - 1].timestamp - points[0].timestamp)
                        : 0
                    )}
                    stroke="white"
                    strokeWidth="2"
                  />
                )}

                {/* Temperature marker with interpolated flight color */}
                {showTemperature && (
                  <circle
                    cx={temperatureToX(activePoint.temperature, activePoint.pressure, skewOptions)}
                    cy={pressureToY(activePoint.pressure, skewOptions)}
                    r="6"
                    fill={interpolateTwoColors(
                      START_COLOR,
                      END_COLOR,
                      (activePoint.timestamp - points[0].timestamp) /
                        Math.max(1, points[points.length - 1].timestamp - points[0].timestamp)
                    )}
                    stroke="white"
                    strokeWidth="2.5"
                  />
                )}
              </g>
            )}
          </g>

          {/* Left Y-Axis (Pressure in hPa) */}
          <g>
            {isobars.map((bar) => (
              <g key={`lbl-bar-${bar.pressure}`}>
                <text
                  x={margin.left - 10}
                  y={bar.y + 4}
                  textAnchor="end"
                  fill="#687088"
                  fontSize="11"
                  fontWeight="500"
                >
                  {bar.pressure}
                </text>
                <line
                  x1={margin.left - 4}
                  y1={bar.y}
                  x2={margin.left}
                  y2={bar.y}
                  stroke="#cbd5e1"
                  strokeWidth="1"
                />
              </g>
            ))}
            <text
              x={18}
              y={margin.top + plotHeight / 2}
              textAnchor="middle"
              transform={`rotate(-90 18 ${margin.top + plotHeight / 2})`}
              fill="#687088"
              fontSize="12"
              fontWeight="600"
            >
              Luftdruck (hPa)
            </text>
          </g>

          {/* Bottom X-Axis (Temperature in °C) */}
          <g>
            {isotherms
              .filter(iso => iso.temperature >= tMin && iso.temperature <= tMax && iso.temperature % 10 === 0)
              .map(iso => {
                const x = temperatureToX(iso.temperature, skewOptions.pMax, skewOptions);
                return (
                  <g key={`lbl-iso-${iso.temperature}`}>
                    <line
                      x1={x}
                      y1={margin.top + plotHeight}
                      x2={x}
                      y2={margin.top + plotHeight + 4}
                      stroke="#cbd5e1"
                      strokeWidth="1"
                    />
                    <text
                      x={x}
                      y={margin.top + plotHeight + 18}
                      textAnchor="middle"
                      fill={iso.isFreezing ? FREEZING_COLOR : '#687088'}
                      fontSize="11"
                      fontWeight={iso.isFreezing ? '700' : '500'}
                    >
                      {iso.temperature}
                    </text>
                  </g>
                );
              })}
            <text
              x={margin.left + plotWidth / 2}
              y={height - 6}
              textAnchor="middle"
              fill="#687088"
              fontSize="12"
              fontWeight="600"
            >
              Temperatur (°C)
            </text>
          </g>
        </svg>

        {/* Floating Custom Tooltip following cursor */}
        {activePoint && (() => {
          const ptX = temperatureToX(activePoint.temperature, activePoint.pressure, skewOptions);
          const ptY = pressureToY(activePoint.pressure, skewOptions);
          const anchorX = mousePos ? mousePos.x : ptX;
          const anchorY = mousePos ? mousePos.y : ptY;

          const tooltipWidth = 195;
          const flipX = anchorX + 16 + tooltipWidth > containerWidth - 10;
          const leftPos = flipX ? Math.max(8, anchorX - tooltipWidth - 14) : anchorX + 14;
          const topPos = Math.max(margin.top, Math.min(height - 180, anchorY - 24));

          return (
            <div
              style={{
                position: 'absolute',
                left: `${leftPos}px`,
                top: `${topPos}px`,
                width: `${tooltipWidth}px`,
                background: 'white',
                border: '1px solid #e8eaf0',
                borderRadius: '6px',
                padding: '8px 12px',
                fontSize: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                fontFamily: 'inherit',
                pointerEvents: 'none',
                zIndex: 20,
                transition: mousePos ? 'none' : 'all 0.15s ease'
              }}
            >
              <div style={{ color: '#687088', margin: '0 0 4px', fontWeight: 600 }}>
                {formatTime(activePoint.timestamp)}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '2px 8px', color: '#475569' }}>
                <span>Luftdruck:</span>
                <strong style={{ color: '#0f172a', textAlign: 'right' }}>{activePoint.pressure.toFixed(1)} hPa</strong>

                <span>Temperatur:</span>
                <strong style={{ color: '#dc2626', textAlign: 'right' }}>{activePoint.temperature.toFixed(2)} °C</strong>

                {activePoint.dewpoint !== undefined && (
                  <>
                    <span>Taupunkt:</span>
                    <strong style={{
                      color: interpolateTwoColors(
                        DEWPOINT_START_COLOR,
                        DEWPOINT_END_COLOR,
                        points.length > 1
                          ? (activePoint.timestamp - points[0].timestamp) /
                            Math.max(1, points[points.length - 1].timestamp - points[0].timestamp)
                          : 0
                      ),
                      textAlign: 'right'
                    }}>
                      {activePoint.dewpoint.toFixed(2)} °C
                    </strong>
                  </>
                )}

                {activePoint.relativeHumidity !== undefined && (
                  <>
                    <span>Rel. Feuchte:</span>
                    <strong style={{ color: '#0284c7', textAlign: 'right' }}>{activePoint.relativeHumidity.toFixed(1)} %</strong>
                  </>
                )}

                {activePoint.altitude !== undefined && (
                  <>
                    <span>Höhe (GNSS):</span>
                    <strong style={{ color: '#2563eb', textAlign: 'right' }}>{activePoint.altitude.toFixed(0)} m</strong>
                  </>
                )}
              </div>
              <div style={{ marginTop: '5px', fontSize: '10px', color: '#9aa0ad', borderTop: '1px solid #f1f5f9', paddingTop: '4px' }}>
                Klicken, um in Tabelle anzuzeigen
              </div>
            </div>
          );
        })()}
      </div>

      {/* Flight Time Progression Gradient Legend */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px',
        marginTop: '12px',
        fontSize: '12px',
        color: '#687088',
        flexWrap: 'wrap'
      }}>
        {/* Freezing level badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: '16px',
            height: '0px',
            borderTop: `2px dashed ${FREEZING_COLOR}`,
            display: 'inline-block'
          }} />
          <span style={{ color: '#475569', fontWeight: 500 }}>0°C Frostgrenze</span>
        </div>

        {/* Dewpoint Legend */}
        {hasDewpoint && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 600, color: '#475569' }}>Taupunkt:</span>
            <span>Start</span>
            <div style={{
              width: '100px',
              height: '5px',
              borderRadius: '3px',
              background: `linear-gradient(to right, ${DEWPOINT_START_COLOR}, ${DEWPOINT_END_COLOR})`
            }} />
            <span>Ende</span>
          </div>
        )}

        {/* Temperature Legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: 600, color: '#475569' }}>Temperatur:</span>
          <span>Start</span>
          <div style={{
            width: '100px',
            height: '5px',
            borderRadius: '3px',
            background: `linear-gradient(to right, ${START_COLOR}, ${END_COLOR})`
          }} />
          <span>Ende</span>
        </div>
      </div>
    </>
  )}
</div>
  );
};

export default React.memo(SkewTChart);
